﻿import { Component } from "react";
import * as Sentry from "@sentry/react";
import PropTypes from "prop-types";

const P      = "#2596be";
const P_DARK = "#1a7a9e";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const isChunkError =
      error?.message?.includes("Failed to fetch dynamically imported module") ||
      error?.message?.includes("error loading dynamically imported module") ||
      error?.message?.includes("Importing a module script failed") ||
      error?.name === "ChunkLoadError";

    if (isChunkError) {
      const alreadyReloaded = sessionStorage.getItem("chunk-reload-attempted");
      if (!alreadyReloaded) {
        sessionStorage.setItem("chunk-reload-attempted", "1");
        // El SW de la PWA puede servir el index.html viejo desde caché aunque
        // se recargue la página. Hay que desregistrarlo y borrar los caches de
        // Workbox antes del reload para que el navegador haga un fetch limpio.
        const doReload = () => window.location.reload();
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker
            .getRegistrations()
            .then((regs) => Promise.all(regs.map((r) => r.unregister())))
            .then(() => caches.keys())
            .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
            .finally(doReload);
        } else {
          doReload();
        }
        return;
      }
      sessionStorage.removeItem("chunk-reload-attempted");
    }

    Sentry.captureException(error, { extra: info });
  }

  render() {
    if (this.state.hasError) {
      const { inline = false } = this.props;

      const card = (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: inline ? "2rem 1.5rem" : "3rem 2rem",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: inline ? 40 : 56, marginBottom: 12 }}>⚠️</div>
          <h2
            style={{
              color: "#1a1a2e",
              marginBottom: 8,
              fontSize: inline ? 18 : 22,
              fontWeight: 700,
              margin: 0,
            }}
          >
            Algo salió mal
          </h2>
          <p
            style={{
              color: "#666",
              marginBottom: 20,
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {inline
              ? "Esta sección tuvo un error inesperado. Puedes intentar de nuevo o navegar a otra sección."
              : "Ocurrió un error inesperado. El equipo técnico ha sido notificado automáticamente. Por favor recarga la página para continuar."}
          </p>

          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {inline && (
              <button
                onClick={() => this.setState({ hasError: false })}
                style={{
                  background: "transparent",
                  color: P,
                  border: `1.5px solid ${P}`,
                  borderRadius: 8,
                  padding: "9px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Intentar de nuevo
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                background: `linear-gradient(90deg, ${P_DARK} 0%, ${P} 100%)`,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "9px 20px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(37,150,190,0.35)",
              }}
            >
              Recargar página
            </button>
          </div>
        </div>
      );

      if (inline) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "3rem 1rem",
            }}
          >
            {card}
          </div>
        );
      }

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "2rem",
            background: "#f8f9fa",
          }}
        >
          {card}
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  inline: PropTypes.bool,
};

export default ErrorBoundary;
